import {
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material'
import {
  DataGrid,
  GridCellParams,
  GridColDef,
  GridFilterModel,
  GridRowsProp,
} from '@mui/x-data-grid'
import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import { useEffect, useState } from 'react'

import {
  ProviderInformation,
  ProviderUser,
} from '../../../../providers/ProviderDetails/types'
import {
  AccessRoleList,
  Provider,
  ProviderList,
} from '../../../../providers/ProvidersProvider/types'
import { themeColors } from '../../../../theme'
import { formatProvidersForTable } from '../utilities'

dayjs.extend(customParseFormat)

const RenderCOLUMNS = (isAdmin: boolean): GridColDef[] => {
  if (isAdmin) {
    return [
      { field: 'isOnline', headerName: 'Online Status', width: 200 },
      { field: 'providerName', headerName: 'Provider Name', width: 200 },
      { field: 'roles', headerName: 'Provider Role', width: 160 },
      { field: 'accessRoles', headerName: 'Access Role', width: 160 },
    ].map((col) => ({ ...col, disableColumnMenu: true }))
  } else {
    return [
      { field: 'isOnline', headerName: 'Online Status', width: 200 },
      { field: 'providerName', headerName: 'Provider Name', width: 200 },
      { field: 'roles', headerName: 'Provider Role', width: 160 },
    ].map((col) => ({ ...col, disableColumnMenu: true }))
  }
}

interface ListViewProps {
  myProviderDetails: ProviderUser | null
  providers: ProviderList
  providerAccessRoles: AccessRoleList
  selectedProvider: Provider | null
  selectedScheduleProvider: ProviderInformation | null
  handleEditProviderModal: (providerId: number) => void
  handleEditScheduleProviderModal: (selectedProviderID: number) => void
  isEditLoading: boolean
}

export const ListView = ({
  myProviderDetails,
  providers,
  selectedProvider,
  handleEditProviderModal,
  handleEditScheduleProviderModal,
  isEditLoading,
}: ListViewProps) => {
  const [searchField, setSearchField] = useState<string | null>('Provider Name')
  const [searchKeyword, setSearchKeyword] = useState<string | null>(null)
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] })
  const [OPTIONS, setOPTIONS] = useState<string[]>([])
  const [dataColumns, setDataColumns] = useState<GridColDef[]>([])
  const [pageSize, setPageSize] = useState<number>(20)

  useEffect(() => {
    const isAdmin = myProviderDetails?.accessRoles?.some((role) =>
      role.description.includes('Admin')
    )

    if (typeof isAdmin === 'boolean') {
      const COLUMNS = RenderCOLUMNS(isAdmin)
      const finalColumns = [...COLUMNS]

      if (isAdmin) {
        finalColumns.push({
          field: 'actions',
          headerName: ' ',
          width: 120,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridCellParams) =>
            isEditLoading &&
            selectedProvider &&
            selectedProvider.providerId === Number(params.id) ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <CircularProgress size={20} />
              </Box>
            ) : (
              <Button
                id={`edit-role-btn-${Number(params.id)}`}
                sx={{
                  minWidth: '100%',
                }}
                variant='contained'
                color='warning'
                onClick={() => {
                  handleEditProviderModal(Number(params.id))
                }}
              >
                Edit Access Role
              </Button>
            ),
        })

        finalColumns.push({
          field: 'scheduleLinks',
          headerName: ' ',
          width: 120,
          sortable: false,
          filterable: false,
          disableColumnMenu: true,
          renderCell: (params: GridCellParams) => {
            return (
              <Button
                id={`edit-role-btn-${Number(params.id)}`}
                sx={{
                  minWidth: '100%',
                }}
                variant='contained'
                color='warning'
                onClick={() => {
                  handleEditScheduleProviderModal(Number(params.id))
                }}
              >
                Schedule Links
              </Button>
            )
          },
        })
      }

      setDataColumns(finalColumns)

      const searchOptions = COLUMNS.filter((col: GridColDef) =>
        ['providerName', 'roles', 'accessRoles', 'isActive'].includes(col.field)
      ).map((col: GridColDef) => col.headerName || col.field)

      setOPTIONS(searchOptions)
    }
  }, [myProviderDetails, providers, isEditLoading, selectedProvider])

  useEffect(() => {
    if (searchField && searchKeyword) {
      setFilterModel({
        items: [
          {
            columnField: dataColumns.find(
              (dc) => dc.headerName === searchField
            )!.field,
            operatorValue: 'contains',
            value: searchKeyword,
          },
        ],
      })
    } else {
      setFilterModel({ items: [] })
    }
  }, [searchField, searchKeyword, dataColumns])

  const formattedRows: GridRowsProp =
    providers?.map((pg: Provider) => formatProvidersForTable(pg)) || []

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'flex-start',
          marginBottom: '10px',
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '18px',
            lineHeight: '125%',
          }}
        >
          Providers View
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          margin: '20px 0 30px',
          width: '675px',
        }}
      >
        <Box sx={{ width: '225px' }}>
          <Typography
            sx={{
              fontWeight: 700,
              fontSize: '14px',
              marginBottom: '5px',
            }}
          >
            SEARCH BY
          </Typography>
          <Autocomplete
            options={OPTIONS}
            sx={{ width: 'auto' }}
            onChange={(_, data: string | null) => {
              setSearchField(data)
              setSearchKeyword(null)
            }}
            value={searchField}
            renderInput={(params) => (
              <TextField
                {...params}
                size='small'
                variant='outlined'
                id='SearchByField'
                placeholder='Select field to search by'
              />
            )}
          />
        </Box>

        {searchField && (
          <Box sx={{ width: '425px' }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: '14px',
                marginBottom: '5px',
              }}
            >
              KEYWORD
            </Typography>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <TextField
                size='small'
                variant='outlined'
                sx={{ height: '35px', width: '325px' }}
                onChange={(e) => setSearchKeyword(e.currentTarget.value)}
                value={searchKeyword || ''}
                placeholder={`Type here to search the table below by ${searchField.toLowerCase()}`}
              />
              <Box>
                <Button
                  variant='outlined'
                  sx={{ minWidth: '75px', marginLeft: '20px' }}
                  onClick={() => {
                    setSearchKeyword(null)
                    setSearchField(null)
                  }}
                >
                  Clear
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          height: '100%',
        }}
      >
        <Box sx={{ width: '100%', flexGrow: 1, height: '600px' }}>
          {providers && (
            <DataGrid
              getRowId={(row) => row.providerId}
              rows={formattedRows}
              columns={dataColumns}
              pageSize={pageSize}
              rowsPerPageOptions={[10, 25, 50]}
              onPageSizeChange={(v) => setPageSize(v)}
              rowHeight={70}
              filterModel={filterModel}
              disableColumnFilter={true}
              isRowSelectable={() => false}
              sx={{
                fontFamily: 'Lato',
                fontStyle: 'normal',
                fontWeight: 400,
                fontSize: '14px',
                lineHeight: '100%',
                verticalAlign: 'center',
                border: 'none',
                '& .MuiDataGrid-cell:active, .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within, .MuiDataGrid-cell:hover':
                  {
                    border: 'none',
                    outline: 'none',
                  },
                '& .MuiTableCell-root': {
                  border: 'none',
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:hover': {
                  cursor: 'pointer',
                },
                '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainer:focus, .MuiDataGrid-columnHeaderTitleContainer:focus-within':
                  {
                    fontWeight: 700,
                    fontSize: '14px',
                    lineHeight: '100%',
                    textTransform: 'uppercase',
                    border: 'none',
                    outline: 'none',
                  },
                '& .MuiDataGrid-iconButtonContainer svg': {
                  color: themeColors.blue,
                },
                '& .MuiDataGrid-iconSeparator': {
                  display: 'none',
                },
                '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within':
                  {
                    border: 'none',
                    outline: 'none',
                  },
                '& .MuiDataGrid-row.even': {
                  backgroundColor: themeColors.gray99,
                },
                '& .MuiDataGrid-row.even:hover, .MuiDataGrid-row.odd:hover': {
                  backgroundColor: themeColors.blue90,
                },
              }}
              getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
              }
              components={{
                NoResultsOverlay: () => (
                  <Box
                    sx={{
                      background: themeColors.white,
                      height: '100%',
                      width: '100%',
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      sx={{
                        fontWeight: 700,
                        fontSize: '14px',
                        color: themeColors.blue,
                      }}
                    >
                      NO RESULTS
                    </Typography>
                  </Box>
                ),
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}
